"use client";
import React, { Fragment } from "react";
import Link from "next/link";
import { Stack, VStack, Center } from "@chakra-ui/react";
import { scrollToElement } from "@/utils/scrollToElement";
import { FiArrowUpRight } from "@onemind-services-llc/react-icons-ng/fi";
import { Heading, Text, Button } from "@/elements";
import { Balancer } from "react-wrap-balancer";

type ButtonProps = {
  label: string;
  link: string;
  target?: React.HTMLAttributeAnchorTarget;
  btn2?: {
    label: string;
    link: string;
    target?: React.HTMLAttributeAnchorTarget;
  };
};

export type HeroCardProps = {
  backgroundImage?: string;
  button?: ButtonProps;
  content?: React.ReactNode;
  title: string;
};

const HeroCard = (props: HeroCardProps) => {
  const {
    backgroundImage = "/background/banner-bg4.png",
    button,
    content,
    title,
  } = props;

  return (
    <Center
      backgroundImage={backgroundImage}
      backgroundPosition={"center"}
      backgroundSize={"cover"}
      bgColor={"primary.200"}
      borderRadius={"16px"}
      color={"white"}
      px={{ base: 4, sm: 8, md: 12 }}
      py={{ base: 8, sm: 16, md: 24 }}
      minH={{ base: "440px", md: "400px" }}
    >
      <VStack gap={8}>
        <Balancer>
          <Heading as="h2" fontWeight={"400"} textAlign={"center"} whiteSpace="pre-line">
            {title}
          </Heading>
        </Balancer>
        <Balancer>
          {content && (
            <Fragment>
              {typeof content === "string" ? (
                <Text textAlign={"center"} variant={"body1"}>
                  {content}
                </Text>
              ) : (
                content
              )}
            </Fragment>
          )}
        </Balancer>
        <Stack
          direction={{ base: "column", sm: "row" }}
          gap={4}
          width="100%"
          justify={"center"}
        >
          {button && <IntroButton {...button} />}
          {button?.btn2 && (
            <IntroButton
              label={button.btn2.label}
              link={button.btn2.link}
              target={button.btn2.target}
            />
          )}
        </Stack>
      </VStack>
    </Center>
  );
};

export default HeroCard;

const IntroButton = (props: ButtonProps) => {
  const { label, link, target } = props;
  const handleClick = () => {
    const elementId = link.replace("#", "");
    scrollToElement(elementId, 120);
  };

  return (
    <Fragment>
      {link.startsWith("#") ? (
        <Button
          bgColor={"white"}
          color={"primary.200"}
          rightIcon={<FiArrowUpRight />}
          py={{ base: 7, sm: 6 }}
          px={6}
          _hover={{ textDecoration: "none" }}
          onClick={handleClick}
          whiteSpace="normal"
        >
          {label}
        </Button>
      ) : (
        <Button
          as={Link}
          bgColor={"white"}
          color={"primary.200"}
          href={link}
          rightIcon={<FiArrowUpRight />}
          target={target}
          p={6}
          _hover={{ textDecoration: "none" }}
          whiteSpace="normal"
        >
          {label}
        </Button>
      )}
    </Fragment>
  );
};
